import { Button, DataTable } from 'shared/components/ui';
import React, { useState, useCallback } from 'react';
import { useAuth } from '../../../core/context/AuthContext';
import Header from '../../../shared/components/layout/Header';
import Footer from '../../../shared/components/layout/Footer';
import { Loading } from '../../../shared/components';
import { ToastContainer } from '../../../shared/components/Toast';
import { contabilidadAPI } from '../services/contabilidadAPI';
import { exportResumen, exportFacturacionCentro, exportIngresos, exportEgresos } from '../utils/excelContabilidad';
import CargaFacturasOS from './CargaFacturasOS';
import GastosFijos from './GastosFijos';
import EntradasMensuales from './EntradasMensuales';
import './ContabilidadPage.css';

const MESES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio',
               'Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];

const CENTROS_GRUPOS = [
  { key: 'privados', label: 'Privados',  color: '#3b82f6' },
  { key: 'nsc',      label: 'NSC',       color: '#10b981' },
  { key: 'nsl',      label: 'NSL',       color: '#f59e0b' },
  { key: 'fleciba',  label: 'Fleciba',   color: '#8b5cf6' },
];

const fmt = (n) => `$${Number(n || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}`;

// ── Tabla genérica ──────────────────────────────────────────────────────────
const TablaOS = ({ items }) => {
  if (!items?.length) return <p className="cont-empty-text">Sin datos para el período.</p>;
  return (
    <div className="cont-tabla-wrap">
      <DataTable className="cont-tabla">
        <thead>
          <tr>
            <th>Fecha</th><th>Apellido</th><th>Nombre</th><th>DNI</th>
            <th>Nro Afiliado</th><th>Práctica</th><th>Código</th>
            <th>Nro Factura</th><th>Importe</th><th>Obra Social</th>
          </tr>
        </thead>
        <tbody>
          {items.map((r, i) => (
            <tr key={r.idFacturacionMensual ?? i}>
              <td>{r.fechaFacturacion ? new Date(r.fechaFacturacion).toLocaleDateString('es-AR') : '-'}</td>
              <td>{r.apellido}</td>
              <td>{r.nombre}</td>
              <td>{r.dni}</td>
              <td>{r.nroAfiliado}</td>
              <td>{r.practica}</td>
              <td>{r.codigoPractica}</td>
              <td>{r.nroFactura}</td>
              <td className="cont-td-num">{fmt(r.importe)}</td>
              <td>{r.obraSocial}</td>
            </tr>
          ))}
        </tbody>
      </DataTable>
    </div>
  );
};

// ── Componente principal ────────────────────────────────────────────────────
const ContabilidadPage = () => {
  const { user } = useAuth();

  const mesActual = new Date().getMonth() === 0 ? 12 : new Date().getMonth();
  const anioActual = new Date().getMonth() === 0 ? new Date().getFullYear() - 1 : new Date().getFullYear();

  const [vistaActiva, setVistaActiva]   = useState('resumen');
  const [mes, setMes]                   = useState(mesActual);
  const [anio, setAnio]                 = useState(anioActual);
  const [loading, setLoading]           = useState(false);
  const [toast, setToast]               = useState(null);

  // datos por sección
  const [resumen, setResumen]           = useState(null);
  const [facturacionOS, setFacturacionOS] = useState(null);
  const [centroActivo, setCentroActivo] = useState(null);   // key del grupo
  const [detalleCentro, setDetalleCentro] = useState(null);
  const [ingresos, setIngresos]         = useState(null);
  const [egresos, setEgresos]           = useState(null);
  const [reporte, setReporte]           = useState(null);

  const tabs = [
    { id: 'resumen',      label: 'Resumen General' },
    { id: 'facturacion',  label: 'Facturación OS' },
    { id: 'ingresos',     label: 'Ingresos' },
    { id: 'egresos',      label: 'Egresos' },
    { id: 'reportes',     label: 'Reportes' },
    { id: 'carga-facturas', label: 'Carga de Facturas' },
    { id: 'gastos-fijos', label: 'Gastos Fijos' },
    { id: 'entradas-mensuales', label: 'Entradas Mensuales' },
  ];

  const handleBuscar = useCallback(async () => {
    setLoading(true);
    try {
      switch (vistaActiva) {
        case 'resumen': {
          const data = await contabilidadAPI.getResumenMensual(mes, anio);
          setResumen(data);
          break;
        }
        case 'facturacion': {
          const data = await contabilidadAPI.getFacturacionOSPorCentro(mes, anio);
          setFacturacionOS(data);
          setDetalleCentro(null);
          setCentroActivo(null);
          break;
        }
        case 'ingresos': {
          const data = await contabilidadAPI.getIngresosPorObraSocial(mes, anio);
          setIngresos(data);
          break;
        }
        case 'egresos': {
          const data = await contabilidadAPI.getEgresosMensuales(mes, anio);
          setEgresos(data);
          break;
        }
        case 'reportes': {
          const [comp, centros] = await Promise.all([
            contabilidadAPI.getReporteComparativo(6),
            contabilidadAPI.getReporteCentros(mes, anio),
          ]);
          setReporte({ comparativo: comp, centros });
          break;
        }
        default: break;
      }
    } catch {
      setToast({ message: 'Error al cargar los datos', type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [vistaActiva, mes, anio]);

  const handleVerDetalleCentro = async (grupoKey) => {
    const grupo = CENTROS_GRUPOS.find(g => g.key === grupoKey);
    if (!grupo) return;
    // usamos el primer idCentro del grupo como representativo; el backend filtra por idCentro exacto
    // Para ver todos los del grupo usamos FacturacionOSPorCentro ya cargado
    setCentroActivo(grupoKey);
    setDetalleCentro(facturacionOS?.[grupoKey]?.items ?? []);
  };

  return (
    <div className="contabilidad-page">
      <Header />
      <div className="contabilidad-content">

        <div className="page-header">
          <h2 className="page-title-grilla">Contabilidad</h2>
          <span className="page-subtitle">Gestión contable y financiera — {user?.username}</span>
        </div>

        {/* Tabs */}
        <div className="tabs-container">
          {tabs.map(t => (
            <button
              key={t.id}
              className={`tab-button ${vistaActiva === t.id ? 'active' : ''}`}
              onClick={() => { setVistaActiva(t.id); setDetalleCentro(null); setCentroActivo(null); }}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Filtros */}
        {vistaActiva !== 'carga-facturas' && vistaActiva !== 'gastos-fijos' && vistaActiva !== 'entradas-mensuales' && (
        <div className="filtros-container">
          <div className="form-group">
            <label>Mes</label>
            <select value={mes} onChange={e => setMes(parseInt(e.target.value))} className="form-control">
              {MESES.map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label>Año</label>
            <select value={anio} onChange={e => setAnio(parseInt(e.target.value))} className="form-control">
              {Array.from({ length: 9 }, (_, i) => 2018 + i).map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div className="form-group button-group">
            <Button onClick={handleBuscar} className="btn btn-primary">Buscar</Button>
          </div>
        </div>
        )}

        {/* Filtros período para tabs con búsqueda propia */}
        {(vistaActiva === 'gastos-fijos' || vistaActiva === 'entradas-mensuales') && (
        <div className="filtros-container">
          <div className="form-group">
            <label>Mes</label>
            <select value={mes} onChange={e => setMes(parseInt(e.target.value))} className="form-control">
              {MESES.map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label>Año</label>
            <select value={anio} onChange={e => setAnio(parseInt(e.target.value))} className="form-control">
              {Array.from({ length: 9 }, (_, i) => 2018 + i).map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
        </div>
        )}

        {loading && <Loading />}

        {/* ── RESUMEN ── */}
        {vistaActiva === 'resumen' && !loading && (
          <div className="contabilidad-section">
            {resumen ? (
              <>
                <div className="totales-container">
                  <div className="card-total card-ingresos">
                    <span className="card-icon">💰</span>
                    <h4>Total Ingresos OS</h4>
                    <strong>{fmt(resumen.totalIngresos)}</strong>
                  </div>
                  <div className="card-total card-egresos">
                    <span className="card-icon">📤</span>
                    <h4>Total Egresos</h4>
                    <strong>{fmt(resumen.totalEgresos)}</strong>
                  </div>
                  <div className={`card-total ${resumen.resultadoNeto >= 0 ? 'card-resultado' : 'card-egresos'}`}>
                    <span className="card-icon">📊</span>
                    <h4>Resultado Neto</h4>
                    <strong>{fmt(resumen.resultadoNeto)}</strong>
                  </div>
                  <div className="card-total card-pendiente">
                    <span className="card-icon">🏥</span>
                    <h4>Egresos Técnicos</h4>
                    <strong>{fmt(resumen.egresosTecnicos)}</strong>
                  </div>
                  <div className="card-total card-pendiente">
                    <span className="card-icon">👨‍⚕️</span>
                    <h4>Egresos Médicos</h4>
                    <strong>{fmt(resumen.egresosMedicos)}</strong>
                  </div>
                </div>

                {resumen.detalleIngresosOS?.length > 0 && (
                  <div style={{ marginTop: 24 }}>
                    <div className="cont-section-header">
                      <span className="cont-section-icon">🏥</span>
                      <h3 className="cont-section-title">Ingresos por Obra Social</h3>
                      <span className="cont-section-badge">{resumen.detalleIngresosOS.length} obras sociales</span>
                      <Button className="btn-export" onClick={() => exportResumen(resumen.detalleIngresosOS, mes, anio)}>⬇ Exportar Excel</Button>
                    </div>
                    <div className="cont-tabla-wrap">
                      <DataTable className="cont-tabla">
                        <thead><tr><th>Obra Social</th><th>Cantidad</th><th>Total</th></tr></thead>
                        <tbody>
                          {resumen.detalleIngresosOS.map((r, i) => (
                            <tr key={i}>
                              <td>{r.nombre}</td>
                              <td>{r.cantidad}</td>
                              <td className="cont-td-num">{fmt(r.total)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </DataTable>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="contabilidad-empty">
                <div className="empty-icon">📋</div>
                <p>Seleccioná un período y presioná <strong>Buscar</strong> para ver el resumen contable.</p>
              </div>
            )}
          </div>
        )}

        {/* ── FACTURACIÓN OS POR CENTRO ── */}
        {vistaActiva === 'facturacion' && !loading && (
          <div className="contabilidad-section">
            {facturacionOS ? (
              <>
                {/* Cards de grupos */}
                {!detalleCentro && (
                  <>
                    <div className="totales-container">
                      {CENTROS_GRUPOS.map(g => (
                        <div
                          key={g.key}
                          className="card-total card-centro"
                          style={{ borderLeftColor: g.color, cursor: 'pointer' }}
                          onClick={() => handleVerDetalleCentro(g.key)}
                        >
                          <span className="card-icon">🏢</span>
                          <h4>{g.label}</h4>
                          <strong style={{ color: g.color }}>{fmt(facturacionOS[g.key]?.total)}</strong>
                          <span className="card-sub">{facturacionOS[g.key]?.cantidad ?? 0} prácticas</span>
                          <span className="card-ver">Ver detalle →</span>
                        </div>
                      ))}
                    </div>
                    <div className="cont-total-general">
                      Total General: <strong>{fmt(facturacionOS.totalGeneral)}</strong>
                    </div>
                  </>
                )}

                {/* Detalle de un grupo */}
                {detalleCentro && centroActivo && (
                  <>
                    <div className="cont-detalle-header">
                      <div className="cont-export-bar" style={{marginLeft:'auto',marginBottom:0}}>
                        <Button className="btn-export" onClick={() => exportFacturacionCentro(detalleCentro, CENTROS_GRUPOS.find(g=>g.key===centroActivo)?.label, mes, anio)}>⬇ Exportar Excel</Button>
                      </div>
                      <Button className="btn btn-secondary" onClick={() => { setDetalleCentro(null); setCentroActivo(null); }}>
                        ← Volver
                      </Button>
                      <h3 className="cont-subtitulo">
                        {CENTROS_GRUPOS.find(g => g.key === centroActivo)?.label} —&nbsp;
                        {MESES[mes-1]} {anio}
                      </h3>
                      <span className="cont-total-badge">
                        Total: {fmt(facturacionOS[centroActivo]?.total)}
                      </span>
                    </div>
                    <TablaOS items={detalleCentro} />
                  </>
                )}
              </>
            ) : (
              <div className="contabilidad-empty">
                <div className="empty-icon">🏢</div>
                <p>Buscá un período para ver la facturación de Obras Sociales por centro.</p>
              </div>
            )}
          </div>
        )}

        {/* ── INGRESOS ── */}
        {vistaActiva === 'ingresos' && !loading && (
          <div className="contabilidad-section">
            {ingresos ? (
              <>
                <div className="cont-export-bar">
                  <Button className="btn-export" onClick={() => exportIngresos(ingresos.items, mes, anio)}>⬇ Exportar Excel</Button>
                </div>
                <div className="cont-total-general">
                  Total Ingresos: <strong>{fmt(ingresos.totalGeneral)}</strong>
                </div>
                <div className="cont-tabla-wrap">
                  <DataTable className="cont-tabla">
                    <thead>
                      <tr><th>Obra Social</th><th>Cantidad</th><th>Sin precio</th><th>Total</th></tr>
                    </thead>
                    <tbody>
                      {ingresos.items?.map((r, i) => (
                        <tr key={i}>
                          <td>{r.obraSocial}</td>
                          <td>{r.cantidad}</td>
                          <td>{r.sinPrecio > 0 ? <span className="cont-badge-warn">{r.sinPrecio}</span> : '-'}</td>
                          <td className="cont-td-num">{fmt(r.total)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </DataTable>
                </div>
              </>
            ) : (
              <div className="contabilidad-empty">
                <div className="empty-icon">💵</div>
                <p>Buscá un período para ver los ingresos por Obra Social.</p>
              </div>
            )}
          </div>
        )}

        {/* ── EGRESOS ── */}
        {vistaActiva === 'egresos' && !loading && (
          <div className="contabilidad-section">
            {egresos ? (
              <>
                <div className="cont-export-bar">
                  <Button className="btn-export" onClick={() => exportEgresos(egresos.tecnicos?.items ?? [], egresos.medicos?.items ?? [], mes, anio)}>⬇ Exportar Excel</Button>
                </div>
                <div className="totales-container">
                  <div className="card-total card-egresos">
                    <span className="card-icon">🔧</span>
                    <h4>Técnicos</h4>
                    <strong>{fmt(egresos.tecnicos?.total)}</strong>
                  </div>
                  <div className="card-total card-egresos">
                    <span className="card-icon">👨‍⚕️</span>
                    <h4>Médicos</h4>
                    <strong>{fmt(egresos.medicos?.total)}</strong>
                  </div>
                  <div className="card-total card-resultado">
                    <span className="card-icon">📤</span>
                    <h4>Total Egresos</h4>
                    <strong>{fmt(egresos.totalEgresos)}</strong>
                  </div>
                </div>

                <div className="cont-egresos-grid">
                  <div>
                    <h3 className="cont-subtitulo">Técnicos</h3>
                    <div className="cont-tabla-wrap">
                      <DataTable className="cont-tabla">
                        <thead><tr><th>Nombre</th><th>Cantidad</th><th>Total</th></tr></thead>
                        <tbody>
                          {egresos.tecnicos?.items?.map((r, i) => (
                            <tr key={i}><td>{r.nombre}</td><td>{r.cantidad}</td><td className="cont-td-num">{fmt(r.total)}</td></tr>
                          ))}
                        </tbody>
                      </DataTable>
                    </div>
                  </div>
                  <div>
                    <h3 className="cont-subtitulo">Médicos</h3>
                    <div className="cont-tabla-wrap">
                      <DataTable className="cont-tabla">
                        <thead><tr><th>Nombre</th><th>Cantidad</th><th>Total</th></tr></thead>
                        <tbody>
                          {egresos.medicos?.items?.map((r, i) => (
                            <tr key={i}><td>{r.nombre}</td><td>{r.cantidad}</td><td className="cont-td-num">{fmt(r.total)}</td></tr>
                          ))}
                        </tbody>
                      </DataTable>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="contabilidad-empty">
                <div className="empty-icon">📤</div>
                <p>Buscá un período para ver los egresos de técnicos y médicos.</p>
              </div>
            )}
          </div>
        )}

        {/* ── REPORTES ── */}
        {vistaActiva === 'reportes' && !loading && (
          <div className="contabilidad-section">
            {reporte ? (
              <>
                <h3 className="cont-subtitulo">Comparativo últimos 6 meses</h3>
                <div className="cont-tabla-wrap">
                  <DataTable className="cont-tabla">
                    <thead>
                      <tr><th>Período</th><th>Ingresos</th><th>Egresos</th><th>Resultado</th></tr>
                    </thead>
                    <tbody>
                      {reporte.comparativo?.map((r, i) => (
                        <tr key={i}>
                          <td>{r.label}</td>
                          <td className="cont-td-num cont-ingreso">{fmt(r.ingresos)}</td>
                          <td className="cont-td-num cont-egreso">{fmt(r.egresos)}</td>
                          <td className={`cont-td-num ${r.resultado >= 0 ? 'cont-positivo' : 'cont-negativo'}`}>
                            {fmt(r.resultado)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </DataTable>
                </div>

                {reporte.centros?.length > 0 && (
                  <>
                    <h3 className="cont-subtitulo" style={{ marginTop: 32 }}>
                      Facturación OS por Centro — {MESES[mes-1]} {anio}
                    </h3>
                    <div className="cont-tabla-wrap">
                      <DataTable className="cont-tabla">
                        <thead><tr><th>Centro</th><th>Cantidad</th><th>Total</th></tr></thead>
                        <tbody>
                          {reporte.centros.map((r, i) => (
                            <tr key={i}>
                              <td>{r.nombre}</td>
                              <td>{r.cantidad}</td>
                              <td className="cont-td-num">{fmt(r.total)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </DataTable>
                    </div>
                  </>
                )}
              </>
            ) : (
              <div className="contabilidad-empty">
                <div className="empty-icon">📈</div>
                <p>Buscá un período para ver los reportes comparativos.</p>
              </div>
            )}
          </div>
        )}

        {/* ── CARGA DE FACTURAS OS ── */}
        {vistaActiva === 'carga-facturas' && !loading && (
          <div className="contabilidad-section">
            <CargaFacturasOS onToast={setToast} />
          </div>
        )}

        {/* ── GASTOS FIJOS ── */}
        {vistaActiva === 'gastos-fijos' && !loading && (
          <div className="contabilidad-section">
            <GastosFijos mes={mes} anio={anio} onToast={setToast} />
          </div>
        )}

        {/* ── ENTRADAS MENSUALES ── */}
        {vistaActiva === 'entradas-mensuales' && !loading && (
          <div className="contabilidad-section">
            <EntradasMensuales mes={mes} anio={anio} onToast={setToast} />
          </div>
        )}

      </div>
      <Footer />
      <ToastContainer toast={toast} onClose={() => setToast(null)} />
    </div>
  );
};

export default ContabilidadPage;
